import { api } from "_shared/_stores/api";
import type { AppDispatch } from "_shared/_stores/store-types";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import KnowledgeBaseArticleSchema, {
  KnowledgeBaseLibrary,
  ArticleIndexEntryJSON,
  ArticleRevisionJSON,
  ArticleSearchResultJSON,
  KnowledgeBaseArticleJSON,
} from "knowledge-base/_schemas/knowledge-base-article-schema";
import KnowledgeBaseCategorySchema, {
  KnowledgeBaseCategoryJSON,
  LibraryJSON,
  LibraryJSONZ,
} from "knowledge-base/_schemas/knowledge-base-category-schema";
import KnowledgeBaseArticle from "knowledge-base/knowledge-base-article";
import { z } from "zod";

export type ArticleIndexEntry = ArticleIndexEntryJSON;
export type ArticleSearchResult = ArticleSearchResultJSON;
export type ArticleRevision = ArticleRevisionJSON;

export type ArticleIndexArgs = {
  library?: KnowledgeBaseLibrary;
  /**
   * Whose knowledge base to read. Needed because a firm can open the external library of any client
   * it works for, so `library` alone does not identify one.
   */
  ownerLegalEntityId?: string;
  includeDrafts?: boolean;
  includeArchived?: boolean;
  templatesOnly?: boolean;
};

export type KnowledgeBaseCategory = KnowledgeBaseCategoryJSON;
export type KnowledgeBaseLibraryEntry = LibraryJSON;

export type CreateCategoryArgs = {
  library: KnowledgeBaseLibrary;
  name: string;
  description?: string;
  parentCategoryId?: string;
  position?: number;
};

export type RenameCategoryArgs = {
  categoryId: string;
  name: string;
  description?: string;
};

export type MoveCategoryArgs = {
  categoryId: string;
  parentCategoryId?: string;
  position: number;
};

export type FileArticleArgs = {
  articleId: string;
  categoryId?: string;
  position: number;
};

/**
 * The intended order of one category's articles.
 *
 * Sent as a whole list because server positions are integers: "between these two" is unanswerable
 * when the two are adjacent. It is also one transaction, so a reorder cannot half-apply.
 */
export type OrderArticlesArgs = {
  categoryId?: string;
  articleIds: readonly string[];
};

/** The intended order of the categories under one parent. */
export type OrderCategoriesArgs = {
  parentCategoryId?: string;
  categoryIds: readonly string[];
};

/**
 * Which article, and whose knowledge base it is being read from.
 *
 * The owner is not decoration. Slugs are unique within an organization, not across the platform, so
 * a reader who can open both Attorney Hub's help and their own knowledge base can hold a slug that
 * exists in each; without the owner the server answers with whichever it reaches first.
 */
export type ArticleBySlugArgs = {
  slug: string;
  ownerLegalEntityId?: string;
};

export type ArticleSearchArgs = {
  search: string;
  library?: KnowledgeBaseLibrary;
  pageSize?: number;
};

export type CreateArticleArgs = {
  slug?: string;
  title: string;
  body: string;
  library: KnowledgeBaseLibrary;
  categoryId?: string;
  position?: number;
  templateKey?: string;
  templateSubject?: string;
  publish?: boolean;
};

export type ReviseArticleArgs = {
  articleId: string;
  title: string;
  body: string;
  templateSubject?: string;
  note?: string;
};

/**
 * The publication transitions, named as the routes that perform them. Modelled as one endpoint
 * because they differ only in the verb, and every one of them invalidates the same caches.
 */
export type PublicationArgs = {
  articleId: string;
  action: "publish" | "unpublish" | "archive" | "unarchive";
};

export type RevertArticleArgs = {
  articleId: string;
  revisionNumber: number;
};

/**
 * Points an article at documents the Documents center is already holding.
 *
 * Ids, not files. The upload goes to the documents API first — it owns storage, mime-type
 * validation and size limits — and only the resulting ids are named here, so there is one file
 * pipeline rather than two.
 */
export type AttachArticleDocumentsArgs = {
  articleId: string;
  documentIds: readonly string[];
};

export type DetachArticleDocumentArgs = {
  articleId: string;
  documentId: string;
};

export type PreviewArgs = {
  articleId: string;
  placeholders: Record<string, string>;
};

export type TemplatePreview = z.infer<typeof KnowledgeBaseArticleSchema.TemplatePreviewJSONZ>;

const articleListTag = { type: "KnowledgeBaseArticle" as const, id: "LIST" };
const categoryListTag = { type: "KnowledgeBaseCategory" as const, id: "LIST" };

/**
 * Search results, separately from the tree.
 *
 * An excerpt is built by the server around the term that was searched for, so it is the one part of
 * a saved article the response does not carry and the browser cannot work out. Naming it on its own
 * lets an edit refresh the excerpts without also refetching a tree it has just been handed.
 */
const articleSearchTag = { type: "KnowledgeBaseArticle" as const, id: "SEARCH" };

function articleTags(slugOrId?: string) {
  return slugOrId
    ? [{ type: "KnowledgeBaseArticle" as const, id: slugOrId }, articleListTag]
    : [articleListTag];
}

/**
 * Retitles a saved article wherever the navigation tree is already drawing it.
 *
 * The tree is the expensive read on this screen and the one always on it, and a revise or a revert
 * changes exactly one thing it draws: the title. Everything else the tree is keyed on - the slug,
 * the category, the position, whether the article is published - is untouched by these two routes,
 * so no entry can start or stop belonging to a filtered tree and the list can be edited rather than
 * asked for again.
 *
 * Every cached variant is visited because the index is keyed by its filters, which this mutation
 * never sees.
 */
function retitleInCachedTrees(
  article: KnowledgeBaseArticle,
  dispatch: AppDispatch,
  state: never,
) {
  for (const args of knowledgeBaseApi.util.selectCachedArgsForQuery(state, "getKnowledgeBaseIndex")) {
    dispatch(
      knowledgeBaseApi.util.updateQueryData("getKnowledgeBaseIndex", args, (entries) => {
        const entry = entries.find((candidate) => candidate.id === article.id);
        if (entry) {
          entry.title = article.title;
        }
      }),
    );
  }
}

export const knowledgeBaseApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * The navigable index. Returns only what the signed-in reader is entitled to — the server
     * applies the library rules as a query filter, so an empty tree means "nothing for you here",
     * not "the request was wrong".
     */
    getKnowledgeBaseIndex: build.query<ArticleIndexEntry[], ArticleIndexArgs | void>({
      query: (args) => ({
        url: "knowledge-base/articles",
        params: {
          ...(args?.library ? { library: args.library } : {}),
          ...(args?.ownerLegalEntityId ? { ownerLegalEntityId: args.ownerLegalEntityId } : {}),
          ...(args?.includeDrafts ? { includeDrafts: true } : {}),
          ...(args?.includeArchived ? { includeArchived: true } : {}),
          ...(args?.templatesOnly ? { templatesOnly: true } : {}),
        },
      }),
      rawResponseSchema: z.array(KnowledgeBaseArticleSchema.IndexEntryJSONZ),
      onSchemaFailure: logSchemaFailure,
      // The list tag and nothing else. Tagging the tree with every article it draws meant that
      // saving one article invalidated the whole tree, which is the read this screen can least
      // afford to repeat - and the one thing a save changes in it, the title, is written in
      // directly by retitleInCachedTrees. Anything that changes which articles belong in the tree
      // still invalidates this tag.
      providesTags: [articleListTag],
    }),

    getKnowledgeBaseArticleBySlug: build.query<KnowledgeBaseArticle, ArticleBySlugArgs>({
      query: ({ slug, ownerLegalEntityId }) => ({
        url: `knowledge-base/articles/by-slug/${slug}`,
        params: ownerLegalEntityId ? { owner: ownerLegalEntityId } : {},
      }),
      rawResponseSchema: KnowledgeBaseArticleSchema.ArticleJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: KnowledgeBaseArticleJSON) =>
        KnowledgeBaseArticle.fromJSON(response),
      providesTags: (_result, _error, { slug }) => articleTags(slug),
    }),

    searchKnowledgeBase: build.query<ArticleSearchResult[], ArticleSearchArgs>({
      query: ({ search, library, pageSize }) => ({
        url: "knowledge-base/articles/search",
        params: {
          search,
          ...(library ? { library } : {}),
          ...(pageSize ? { pageSize } : {}),
        },
      }),
      rawResponseSchema: z.array(KnowledgeBaseArticleSchema.SearchResultJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: [articleSearchTag, articleListTag],
    }),

    getKnowledgeBaseArticleRevisions: build.query<ArticleRevision[], string>({
      query: (articleId) => `knowledge-base/articles/${articleId}/revisions`,
      rawResponseSchema: z.array(KnowledgeBaseArticleSchema.RevisionJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, articleId) => articleTags(articleId),
    }),

    createKnowledgeBaseArticle: build.mutation<KnowledgeBaseArticle, CreateArticleArgs>({
      query: (body) => ({ url: "knowledge-base/articles", method: "POST", body }),
      rawResponseSchema: KnowledgeBaseArticleSchema.ArticleJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: KnowledgeBaseArticleJSON) =>
        KnowledgeBaseArticle.fromJSON(response),
      invalidatesTags: [articleListTag],
    }),

    reviseKnowledgeBaseArticle: build.mutation<KnowledgeBaseArticle, ReviseArticleArgs>({
      query: ({ articleId, ...body }) => ({
        url: `knowledge-base/articles/${articleId}`,
        method: "PUT",
        body,
      }),
      rawResponseSchema: KnowledgeBaseArticleSchema.ArticleJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: KnowledgeBaseArticleJSON) =>
        KnowledgeBaseArticle.fromJSON(response),
      // Three reads, none of which this reply can stand in for: the history gains a revision that
      // is not in the body of the reply, an excerpt is built around a search term this route never
      // saw, and the reader's own copy is a KnowledgeBaseArticle that RTK Query has no way to write
      // (see the note on retitleInCachedTrees). The tree, which is the costly one, is not among
      // them - it is retitled in place below.
      invalidatesTags: (result, _error, { articleId }) => [
        { type: "KnowledgeBaseArticle" as const, id: articleId },
        ...(result ? [{ type: "KnowledgeBaseArticle" as const, id: result.slug }] : []),
        articleSearchTag,
      ],
      async onQueryStarted(_args, { dispatch, getState, queryFulfilled }) {
        const { data: article } = await queryFulfilled;
        retitleInCachedTrees(article, dispatch, getState() as never);
      },
    }),

    setKnowledgeBaseArticlePublication: build.mutation<KnowledgeBaseArticle, PublicationArgs>({
      query: ({ articleId, action }) => ({
        url: `knowledge-base/articles/${articleId}/${action}`,
        method: "POST",
      }),
      rawResponseSchema: KnowledgeBaseArticleSchema.ArticleJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: KnowledgeBaseArticleJSON) =>
        KnowledgeBaseArticle.fromJSON(response),
      // The one write here that cannot be patched into the tree. Publishing or archiving changes
      // which articles a filtered index is entitled to list at all, and an entry that should now
      // appear - or stop appearing - cannot be edited into a list it is not in. Invalidating the
      // list covers the article, its history and the excerpts with it, because every one of those
      // reads carries the list tag too.
      invalidatesTags: [articleListTag],
    }),

    revertKnowledgeBaseArticle: build.mutation<KnowledgeBaseArticle, RevertArticleArgs>({
      query: ({ articleId, revisionNumber }) => ({
        url: `knowledge-base/articles/${articleId}/revert`,
        method: "POST",
        body: { revisionNumber },
      }),
      rawResponseSchema: KnowledgeBaseArticleSchema.ArticleJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: KnowledgeBaseArticleJSON) =>
        KnowledgeBaseArticle.fromJSON(response),
      // As for a revise: reverting restores earlier text and records that it did, so the same three
      // reads are refreshed and the tree is retitled rather than refetched.
      invalidatesTags: (result, _error, { articleId }) => [
        { type: "KnowledgeBaseArticle" as const, id: articleId },
        ...(result ? [{ type: "KnowledgeBaseArticle" as const, id: result.slug }] : []),
        articleSearchTag,
      ],
      async onQueryStarted(_args, { dispatch, getState, queryFulfilled }) {
        const { data: article } = await queryFulfilled;
        retitleInCachedTrees(article, dispatch, getState() as never);
      },
    }),

    /**
     * Which knowledge bases this reader can open, and which of them they may write to.
     *
     * Separate from the article index because an empty library still has to appear: a client that
     * has published nothing yet should show as an empty shelf to the firm that works for them, not
     * vanish from the switcher.
     */
    getKnowledgeBaseLibraries: build.query<KnowledgeBaseLibraryEntry[], void>({
      query: () => "knowledge-base/libraries",
      rawResponseSchema: z.array(LibraryJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "KnowledgeBaseLibrary" as const, id: "LIST" }],
    }),

    getKnowledgeBaseCategories: build.query<KnowledgeBaseCategory[], KnowledgeBaseLibrary | void>({
      query: (library) => ({
        url: "knowledge-base/categories",
        params: library ? { library } : {},
      }),
      rawResponseSchema: z.array(KnowledgeBaseCategorySchema.CategoryJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result
          ? [
              ...result.map((category) => ({
                type: "KnowledgeBaseCategory" as const,
                id: category.id,
              })),
              categoryListTag,
            ]
          : [categoryListTag],
    }),

    createKnowledgeBaseCategory: build.mutation<KnowledgeBaseCategory, CreateCategoryArgs>({
      query: (body) => ({ url: "knowledge-base/categories", method: "POST", body }),
      rawResponseSchema: KnowledgeBaseCategorySchema.CategoryJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [categoryListTag],
    }),

    renameKnowledgeBaseCategory: build.mutation<KnowledgeBaseCategory, RenameCategoryArgs>({
      query: ({ categoryId, ...body }) => ({
        url: `knowledge-base/categories/${categoryId}`,
        method: "PUT",
        body,
      }),
      rawResponseSchema: KnowledgeBaseCategorySchema.CategoryJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { categoryId }) => [
        { type: "KnowledgeBaseCategory" as const, id: categoryId },
        categoryListTag,
      ],
    }),

    moveKnowledgeBaseCategory: build.mutation<KnowledgeBaseCategory, MoveCategoryArgs>({
      query: ({ categoryId, ...body }) => ({
        url: `knowledge-base/categories/${categoryId}/move`,
        method: "POST",
        body,
      }),
      rawResponseSchema: KnowledgeBaseCategorySchema.CategoryJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [categoryListTag],
    }),

    removeKnowledgeBaseCategory: build.mutation<void, string>({
      query: (categoryId) => ({
        url: `knowledge-base/categories/${categoryId}`,
        method: "DELETE",
      }),
      invalidatesTags: [categoryListTag],
    }),

    /**
     * Files an article under a category, or loosens it back out of one.
     *
     * Invalidates the article list rather than a single article: moving one changes where it sits
     * in everybody's tree, which is the thing the reader is looking at.
     */
    fileKnowledgeBaseArticle: build.mutation<KnowledgeBaseArticle, FileArticleArgs>({
      query: ({ articleId, ...body }) => ({
        url: `knowledge-base/articles/${articleId}/file`,
        method: "POST",
        body,
      }),
      rawResponseSchema: KnowledgeBaseArticleSchema.ArticleJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: KnowledgeBaseArticleJSON) =>
        KnowledgeBaseArticle.fromJSON(response),
      invalidatesTags: (result, _error, { articleId }) => [
        { type: "KnowledgeBaseArticle" as const, id: articleId },
        ...(result ? [{ type: "KnowledgeBaseArticle" as const, id: result.slug }] : []),
        articleListTag,
      ],
    }),

    orderKnowledgeBaseCategories: build.mutation<void, OrderCategoriesArgs>({
      query: (body) => ({ url: "knowledge-base/categories/order", method: "POST", body }),
      // Re-parenting changes which articles appear under which heading, so the article list is
      // invalidated alongside the category list.
      invalidatesTags: [categoryListTag, articleListTag],
    }),

    orderKnowledgeBaseArticles: build.mutation<void, OrderArticlesArgs>({
      query: (body) => ({ url: "knowledge-base/articles/order", method: "POST", body }),
      // The whole list moves, so the list cache is what has to be refetched. Individual article
      // caches are untouched: reordering changes no article's text.
      invalidatesTags: [articleListTag],
    }),

    /**
     * Attaches already-uploaded documents to an article.
     *
     * Invalidates the article rather than the tree: which forms hang off a page changes nothing
     * the index draws, and the tree is the expensive read on this screen.
     */
    attachKnowledgeBaseDocuments: build.mutation<KnowledgeBaseArticle, AttachArticleDocumentsArgs>({
      query: ({ articleId, documentIds }) => ({
        url: `knowledge-base/articles/${articleId}/documents`,
        method: "POST",
        body: { documentIds },
      }),
      rawResponseSchema: KnowledgeBaseArticleSchema.ArticleJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: KnowledgeBaseArticleJSON) =>
        KnowledgeBaseArticle.fromJSON(response),
      invalidatesTags: (result, _error, { articleId }) => [
        { type: "KnowledgeBaseArticle" as const, id: articleId },
        ...(result ? [{ type: "KnowledgeBaseArticle" as const, id: result.slug }] : []),
      ],
    }),

    /** Removes one attachment. The document itself stays in the Documents center. */
    detachKnowledgeBaseDocument: build.mutation<KnowledgeBaseArticle, DetachArticleDocumentArgs>({
      query: ({ articleId, documentId }) => ({
        url: `knowledge-base/articles/${articleId}/documents/${documentId}`,
        method: "DELETE",
      }),
      rawResponseSchema: KnowledgeBaseArticleSchema.ArticleJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: KnowledgeBaseArticleJSON) =>
        KnowledgeBaseArticle.fromJSON(response),
      invalidatesTags: (result, _error, { articleId }) => [
        { type: "KnowledgeBaseArticle" as const, id: articleId },
        ...(result ? [{ type: "KnowledgeBaseArticle" as const, id: result.slug }] : []),
      ],
    }),

    previewKnowledgeBaseLetter: build.mutation<TemplatePreview, PreviewArgs>({
      query: ({ articleId, placeholders }) => ({
        url: `knowledge-base/articles/${articleId}/preview`,
        method: "POST",
        body: { placeholders },
      }),
      rawResponseSchema: KnowledgeBaseArticleSchema.TemplatePreviewJSONZ,
      onSchemaFailure: logSchemaFailure,
    }),
  }),
});

export const {
  useGetKnowledgeBaseIndexQuery,
  useGetKnowledgeBaseArticleBySlugQuery,
  useSearchKnowledgeBaseQuery,
  useLazySearchKnowledgeBaseQuery,
  useGetKnowledgeBaseArticleRevisionsQuery,
  useCreateKnowledgeBaseArticleMutation,
  useReviseKnowledgeBaseArticleMutation,
  useSetKnowledgeBaseArticlePublicationMutation,
  useRevertKnowledgeBaseArticleMutation,
  usePreviewKnowledgeBaseLetterMutation,
  useGetKnowledgeBaseLibrariesQuery,
  useGetKnowledgeBaseCategoriesQuery,
  useCreateKnowledgeBaseCategoryMutation,
  useRenameKnowledgeBaseCategoryMutation,
  useMoveKnowledgeBaseCategoryMutation,
  useRemoveKnowledgeBaseCategoryMutation,
  useFileKnowledgeBaseArticleMutation,
  useOrderKnowledgeBaseArticlesMutation,
  useOrderKnowledgeBaseCategoriesMutation,
  useAttachKnowledgeBaseDocumentsMutation,
  useDetachKnowledgeBaseDocumentMutation,
} = knowledgeBaseApi;
